import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { 
  HomeIcon, 
  ChartBarIcon, 
  DocumentTextIcon,
  CalendarIcon,
  LightBulbIcon,
  CogIcon,
  PlusIcon,
  BanknotesIcon,
  UsersIcon,
  CloudArrowUpIcon,
  SparklesIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import AddTradeModal from './AddTradeModal'
import { useAuth } from '../contexts/AuthContext'
import { useStats } from '../contexts/StatsContext'
import { api } from '../services/api'
import toast from 'react-hot-toast'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, color: 'from-blue-500 to-cyan-500' },
  { name: 'Trades', href: '/trades', icon: DocumentTextIcon, color: 'from-green-500 to-emerald-500' },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, color: 'from-purple-500 to-violet-500' },
  { name: 'Calendar', href: '/calendar', icon: CalendarIcon, color: 'from-orange-500 to-red-500' },
  { name: 'AI Insights', href: '/ai-insights', icon: LightBulbIcon, color: 'from-yellow-500 to-orange-500' },
  { name: 'P&L', href: '/pnl', icon: BanknotesIcon, color: 'from-pink-500 to-rose-500' },
  { name: 'Social', href: '/social', icon: UsersIcon, color: 'from-teal-500 to-cyan-500' },
  { name: 'Settings', href: '/settings', icon: CogIcon, color: 'from-gray-600 to-gray-700' },
]

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation()
  const [showAddTradeModal, setShowAddTradeModal] = useState(false)
  const { user } = useAuth()
  const { userStats, refreshStats } = useStats()

  const handleAddTrade = async (tradeData) => {
    try {
      // Validate required fields
      if (!tradeData.symbol || !tradeData.quantity || !tradeData.entryPrice || !tradeData.entryDate) {
        toast.error('Please fill in all required fields')
        return
      }

      const formattedData = {
        symbol: tradeData.symbol.toUpperCase(),
        assetType: 'stock',
        side: tradeData.side,
        quantity: parseFloat(tradeData.quantity),
        entryPrice: parseFloat(tradeData.entryPrice),
        exitPrice: tradeData.exitPrice ? parseFloat(tradeData.exitPrice) : undefined,
        entryTime: new Date(tradeData.entryDate + 'T12:00:00.000Z').toISOString(),
        exitTime: tradeData.exitDate ? new Date(tradeData.exitDate + 'T12:00:00.000Z').toISOString() : undefined,
        strategy: tradeData.strategy || undefined,
        notes: tradeData.notes || undefined,
        commission: 0,
        fees: 0
      }

      await api.trades.create(formattedData)
      toast.success('Trade added successfully!')
      
      // Refresh user stats
      refreshStats()
    } catch (error) {
      console.error('Error adding trade:', error)
      const errorMessage = error.response?.data?.error || 'Failed to add trade. Please try again.'
      toast.error(errorMessage)
    }
  }

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    }
  }

  const itemVariants = {
    open: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
    closed: {
      opacity: 0,
      x: -20,
      transition: {
        duration: 0.2
      }
    }
  }

  const UserStatsSection = () => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.9 }}
      className="mt-4 p-4 bg-gradient-to-r from-white/10 to-white/5 rounded-xl border border-white/10"
    >
      <div className="text-center">
        <div className={`text-2xl font-bold ${userStats.monthlyPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {userStats.monthlyPnL >= 0 ? '+' : ''}{userStats.monthlyPnLPercent.toFixed(1)}%
        </div>
        <div className="text-xs text-gray-400">This Month</div>
      </div>
      <div className="flex justify-between mt-3 text-xs">
        <div>
          <div className="text-white font-medium">{userStats.totalTrades}</div>
          <div className="text-gray-400">Trades</div>
        </div>
        <div>
          <div className="text-green-400 font-medium">{userStats.winRate.toFixed(0)}%</div>
          <div className="text-gray-400">Win Rate</div>
        </div>
      </div>
    </motion.div>
  )

  return (
    <>
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Desktop Sidebar - Always visible */}
      <div className="hidden lg:block lg:w-72 lg:flex-shrink-0">
        <div className="flex flex-col h-full bg-gradient-to-b from-slate-900/95 to-slate-800/95 backdrop-blur-xl border-r border-white/10">
          {/* Logo */}
          <div className="flex items-center h-20 px-6 border-b border-white/10">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  AI Trading Journal
                </h1>
                <p className="text-xs text-gray-400">Professional Edition</p>
              </div>
            </motion.div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item, index) => {
              const isActive = location.pathname === item.href
              return (
                <motion.div
                  key={item.name}
                  variants={itemVariants}
                  initial="closed"
                  animate="open"
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.href}
                    className={`
                      group relative flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200
                      ${isActive 
                        ? 'bg-gradient-to-r from-white/20 to-white/10 text-white shadow-lg backdrop-blur-sm border border-white/20' 
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }
                    `}
                  >
                    {/* Active indicator */}
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 rounded-xl"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    
                    {/* Icon with gradient background */}
                    <div className={`
                      relative mr-3 p-2 rounded-lg transition-all duration-200
                      ${isActive 
                        ? `bg-gradient-to-r ${item.color} shadow-lg` 
                        : 'bg-white/10 group-hover:bg-white/20'
                      }
                    `}>
                      <item.icon className={`
                        w-5 h-5 transition-colors duration-200
                        ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'}
                      `} />
                    </div>
                    
                    <span className="relative">{item.name}</span>
                    
                    {/* Hover effect */}
                    {!isActive && (
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/0 to-purple-600/0 group-hover:from-cyan-500/10 group-hover:to-purple-600/10 transition-all duration-200" />
                    )}
                  </Link>
                </motion.div>
              )
            })}
          </nav>

          {/* Quick Actions */}
          <div className="p-4 border-t border-white/10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddTradeModal(true)}
                className="flex items-center justify-center w-full px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Trade
              </motion.button>
            </motion.div>

            <UserStatsSection />
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <motion.div 
        variants={sidebarVariants}
        animate={sidebarOpen ? "open" : "closed"}
        className="fixed inset-y-0 left-0 z-50 w-72 lg:hidden"
      >
        <div className="flex flex-col h-full bg-gradient-to-b from-slate-900/95 to-slate-800/95 backdrop-blur-xl border-r border-white/10">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-6 border-b border-white/10">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  AI Trading Journal
                </h1>
                <p className="text-xs text-gray-400">Professional Edition</p>
              </div>
            </motion.div>

            {/* Mobile close button */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item, index) => {
              const isActive = location.pathname === item.href
              return (
                <motion.div
                  key={item.name}
                  variants={itemVariants}
                  initial="closed"
                  animate="open"
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.href}
                    className={`
                      group relative flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200
                      ${isActive 
                        ? 'bg-gradient-to-r from-white/20 to-white/10 text-white shadow-lg backdrop-blur-sm border border-white/20' 
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }
                    `}
                    onClick={() => setSidebarOpen(false)}
                  >
                    {/* Active indicator */}
                    {isActive && (
                      <motion.div
                        layoutId="activeTabMobile"
                        className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 rounded-xl"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    
                    {/* Icon with gradient background */}
                    <div className={`
                      relative mr-3 p-2 rounded-lg transition-all duration-200
                      ${isActive 
                        ? `bg-gradient-to-r ${item.color} shadow-lg` 
                        : 'bg-white/10 group-hover:bg-white/20'
                      }
                    `}>
                      <item.icon className={`
                        w-5 h-5 transition-colors duration-200
                        ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'}
                      `} />
                    </div>
                    
                    <span className="relative">{item.name}</span>
                    
                    {/* Hover effect */}
                    {!isActive && (
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/0 to-purple-600/0 group-hover:from-cyan-500/10 group-hover:to-purple-600/10 transition-all duration-200" />
                    )}
                  </Link>
                </motion.div>
              )
            })}
          </nav>

          {/* Quick Actions */}
          <div className="p-4 border-t border-white/10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowAddTradeModal(true)
                  setSidebarOpen(false)
                }}
                className="flex items-center justify-center w-full px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Trade
              </motion.button>
            </motion.div>

            <UserStatsSection />
          </div>
        </div>
      </motion.div>
      
      {/* Add Trade Modal */}
      <AddTradeModal 
        isOpen={showAddTradeModal}
        onClose={() => setShowAddTradeModal(false)}
        onSubmit={handleAddTrade}
      />
    </>
  )
}

export default Sidebar